import { AnimatePresence, motion } from 'framer-motion';
import { X, Target, Lightbulb, GraduationCap, AlertCircle, CheckCircle2, Download } from 'lucide-react';
import { generatePDF } from '../services/pdfGenerator';

interface GuideVenteBZTelecomModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function GuideVenteBZTelecomModal({ isOpen, onClose }: GuideVenteBZTelecomModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />
        <motion.div
          id="guide-vente-bz-telecom-content"
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="relative w-[90vw] max-w-5xl max-h-[90vh] bg-slate-950 rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-cyan-500/40"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-cyan-500/40 bg-slate-950/90 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-[0.7rem] uppercase tracking-wide text-cyan-300 font-semibold">
                  Guide de Vente
                </p>
                <h2 className="text-lg md:text-xl font-bold text-white">BZ Telecom – du lead au client live</h2>
                <p className="text-xs text-slate-300/80 mt-0.5">
                  Workflow simple pour un vendeur BZ Telecom : prise de besoin, options claires, réponses aux objections.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => generatePDF('guide-vente-bz-telecom-content', `Guide_Vente_BZ_Telecom_${new Date().toISOString().slice(0, 10)}.pdf`)}
                className="p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/20 text-white transition"
                aria-label="Télécharger le guide en PDF"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/20 text-white transition"
                aria-label="Fermer le guide de vente BZ Telecom"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="prisme-content-scroll flex-1 overflow-y-auto p-6 bg-slate-950/95">
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              {/* Workflow simple */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-cyan-300 flex items-center gap-2 uppercase tracking-wide">
                  <Lightbulb className="w-4 h-4" />
                  Workflow du parfait vendeur
                </h3>
                <ol className="space-y-2 text-slate-100 list-decimal list-inside text-xs md:text-[0.78rem]">
                  <li>
                    <span className="font-semibold text-cyan-200">Lead qualifié (fit rapide)</span>
                    <p className="text-slate-300/80">
                      Comprendre qui est le client, son nombre d&apos;employés, ses sites et si la téléphonie est déjà
                      un irritant réel.
                    </p>
                  </li>
                  <li>
                    <span className="font-semibold text-cyan-200">Prise de besoin structurée</span>
                    <p className="text-slate-300/80">
                      Utiliser les questions de découverte pour cartographier le système actuel, les irritants et les
                      objectifs (fiabilité, télétravail, multi-sites, IA, etc.).
                    </p>
                  </li>
                  <li>
                    <span className="font-semibold text-cyan-200">Scénarios & soumission</span>
                    <p className="text-slate-300/80">
                      Proposer 1–2 options simples (ex.: Essentiel vs Complet) basées sur le configurateur BZ Telecom,
                      avec une phrase claire de valeur pour chaque.
                    </p>
                  </li>
                  <li>
                    <span className="font-semibold text-cyan-200">Validation & objections</span>
                    <p className="text-slate-300/80">
                      Traiter les questions sur le fournisseur actuel, les contrats, la fiabilité, le télétravail et les
                      multi-sites avec les scripts fournis.
                    </p>
                  </li>
                  <li>
                    <span className="font-semibold text-cyan-200">Closing & passage à l’implantation</span>
                    <p className="text-slate-300/80">
                      Confirmer l&apos;option choisie, les dates, les sites impliqués et transférer un dossier complet
                      (questionnaire + schéma) à l&apos;équipe technique.
                    </p>
                  </li>
                </ol>
              </div>

              {/* Questions de prise de besoin */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-emerald-300 flex items-center gap-2 uppercase tracking-wide">
                  <GraduationCap className="w-4 h-4" />
                  Questions de découverte
                </h3>
                <div className="space-y-3 text-xs md:text-[0.78rem] text-slate-200">
                  <div>
                    <p className="font-semibold text-emerald-200 text-[0.7rem] mb-1">1. Contexte & entreprise</p>
                    <ul className="list-disc list-inside space-y-1 text-slate-300/90">
                      <li>Combien d&apos;employés et de postes téléphoniques?</li>
                      <li>Combien de sites physiques et combien de télétravailleurs réguliers?</li>
                      <li>Quel est votre secteur d&apos;activité principal?</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-200 text-[0.7rem] mb-1">
                      2. Téléphonie actuelle & fournisseur
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-slate-300/90">
                      <li>Quel fournisseur / système utilisez-vous actuellement (PBX, VoIP, cloud)?</li>
                      <li>Êtes-vous lié par un contrat? Jusqu&apos;à quand?</li>
                      <li>Quelles sont les 2–3 choses qui vous irritent le plus aujourd&apos;hui?</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-200 text-[0.7rem] mb-1">
                      3. Flux d&apos;appels & fonctionnalités
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-slate-300/90">
                      <li>Décrivez un appel type : accueil, file, transfert, messagerie.</li>
                      <li>
                        Avez-vous besoin d&apos;enregistrements, de files d&apos;attente avancées ou de rapports
                        détaillés?
                      </li>
                      <li>La téléphonie doit-elle s&apos;intégrer à un CRM, Teams ou autres outils?</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-200 text-[0.7rem] mb-1">
                      4. Infrastructure & connectivité
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-slate-300/90">
                      <li>Quel type d&apos;accès Internet avez-vous sur vos sites principaux?</li>
                      <li>Le câblage structuré est-il déjà en place? Y a-t-il des zones problématiques?</li>
                      <li>Vos utilisateurs doivent-ils pouvoir travailler de n&apos;importe où?</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-200 text-[0.7rem] mb-1">
                      5. Décision & budget
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-slate-300/90">
                      <li>Qui doit être impliqué dans la décision finale?</li>
                      <li>Y a-t-il une fenêtre idéale pour le changement (échéance de contrat, projet interne)?</li>
                      <li>Avez-vous une fourchette de budget mensuel en tête pour la téléphonie?</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Objections & scripts */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-orange-300 flex items-center gap-2 uppercase tracking-wide">
                  <AlertCircle className="w-4 h-4" />
                  Objections courantes
                </h3>
                <div className="space-y-3 text-xs md:text-[0.78rem] text-slate-200">
                  <div className="bg-slate-900/70 rounded-lg p-3 border border-slate-700/70">
                    <p className="font-semibold text-orange-300">« On a déjà un fournisseur. »</p>
                    <p className="mt-1 text-slate-300/90">
                      <span className="text-slate-400">Réponse :</span> C&apos;est une bonne nouvelle, ça montre que la
                      téléphonie est importante pour vous. Notre approche, c&apos;est un service local, humain, sans
                      engagement long terme, avec un accompagnement serré sur vos ajustements quotidiens. On peut
                      comparer calmement sur trois axes : qualité, flexibilité, service.
                    </p>
                  </div>
                  <div className="bg-slate-900/70 rounded-lg p-3 border border-slate-700/70">
                    <p className="font-semibold text-orange-300">« On ne veut pas être pris dans un contrat. »</p>
                    <p className="mt-1 text-slate-300/90">
                      <span className="text-slate-400">Réponse :</span> Justement, l&apos;un de nos différenciateurs est
                      l&apos;absence d&apos;engagement long terme. Vous restez parce que ça fonctionne et que le service
                      est bon, pas parce qu&apos;un contrat vous y force.
                    </p>
                  </div>
                  <div className="bg-slate-900/70 rounded-lg p-3 border border-slate-700/70">
                    <p className="font-semibold text-orange-300">
                      « Est-ce que c&apos;est fiable? On ne peut pas se permettre des coupures. »
                    </p>
                    <p className="mt-1 text-slate-300/90">
                      <span className="text-slate-400">Réponse :</span> La plateforme est pensée pour la disponibilité
                      (redondance, monitoring, bonnes pratiques). Avant de signer, on valide ensemble vos besoins de
                      redondance pour que la solution tienne la route dans VOTRE réalité, pas juste en théorie.
                    </p>
                  </div>
                  <div className="bg-slate-900/70 rounded-lg p-3 border border-slate-700/70">
                    <p className="font-semibold text-orange-300">
                      « Avec le télétravail et nos sites multiples, ça va être compliqué. »
                    </p>
                    <p className="mt-1 text-slate-300/90">
                      <span className="text-slate-400">Réponse :</span> La force d&apos;une solution IP hébergée, c&apos;est
                      justement de simplifier le multi-sites et le télétravail. On pense en &quot;extension logique&quot; :
                      vos équipes sont sur le même système, peu importe d&apos;où elles travaillent. On cartographie vos
                      sites et profils pour que ce soit simple à vivre pour eux.
                    </p>
                  </div>
                  <div className="bg-slate-900/70 rounded-lg p-3 border border-slate-700/70">
                    <p className="font-semibold text-orange-300">Bonus IA (toujours en bonus, jamais en promesse vide)</p>
                    <p className="mt-1 text-slate-300/90">
                      L&apos;IA vient après la base : elle aide à mieux router, mieux analyser, mieux rapporter. On ne
                      vend pas de la magie; on promet une base solide, puis on ajoute l&apos;IA là où ça fait une vraie
                      différence pour vos équipes.
                    </p>
                  </div>
                </div>

                <div className="mt-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-[0.7rem] text-emerald-100 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-[2px]" />
                  <p>
                    <span className="font-semibold">Rappel vendeur :</span> ton rôle est de rendre la discussion simple
                    et rassurante. Tu poses les bonnes questions, tu expliques calmement les options, tu ne survends pas
                    l&apos;IA, et tu t&apos;assures que le client comprend exactement ce qu&apos;il achète.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

